import { useEffect, useMemo, useState, type ReactNode } from "react";
import styled from "styled-components";

import {
  AnchoredPopup,
  AnchoredPopupBody,
  AnchoredPopupFooter,
  useAnchoredPopupAnchor,
} from "../../AnchoredPopup";
import { Field } from "../../Field";
import { IconButton } from "../../IconButton";
import { CalendarIcon } from "../../../icons/glyphs/CalendarIcon";
import { PickerRoot, PickerTrigger } from "../pickerTrigger";
import { useTriggerToggle } from "../../foundations/popoverTrigger";
import { Calendar } from "../DatePicker/Calendar";
import {
  addDays,
  formatDate,
  getLocaleWeekStart,
  isSameDay,
  startOfDay,
} from "../DatePicker/dateUtils";
import { useDateSelection } from "../DatePicker/useDateSelection";
import { TimeFields } from "../TimeInput/TimeFields";
import { formatTime, normalizeTime, toMinutes } from "../TimeInput/timeUtils";
import type { TimeValue } from "../TimeInput/types";
import type { DateTimePickerProps, DateTimeValue } from "./types";

const nowTime = (): TimeValue => {
  const d = new Date();
  return { hours: d.getHours(), minutes: d.getMinutes() };
};

const isEndOfDay = (t: TimeValue | null) => t != null && t.hours === 24 && t.minutes === 0;
// 24:00("오늘의 끝") **입력 편의** → 실제 다음날 00:00으로 정규화(RFC-datetime-range-half-open, 단방향). 값·표시는 항상
// canonical(0–23) 유지 — 재오픈 시 24:00을 되살리지 않는다(00:00은 전날로 안 밀림).
const toActual = (v: DateTimeValue): DateTimeValue =>
  v.date && isEndOfDay(v.time) ? { date: addDays(v.date, 1), time: { hours: 0, minutes: 0 } } : v;

// 두 시간 하/상한을 합칠 때: 하한은 더 늦은 쪽(max), 상한은 더 이른 쪽(min)을 취한다.
const laterTime = (a: TimeValue | null, b: TimeValue | null): TimeValue | undefined =>
  a && b ? (toMinutes(a) >= toMinutes(b) ? a : b) : (a ?? b ?? undefined);
const earlierTime = (a: TimeValue | null, b: TimeValue | null): TimeValue | undefined =>
  a && b ? (toMinutes(a) <= toMinutes(b) ? a : b) : (a ?? b ?? undefined);
// 인스턴트 경계가 draftDate와 같은 날일 때만 time-of-day 경계로 반영(교차 검증 RFC-datetime-range-half-open).
const boundaryTime = (
  bound: DateTimeValue | undefined,
  draftDate: Date | null,
): TimeValue | null =>
  bound?.date && bound.time && draftDate && isSameDay(draftDate, bound.date) ? bound.time : null;

/**
 * 날짜 + 시각 선택 컨트롤(RFC-date-time-picker). 하나의 트리거 → AnchoredPopup에 **`Calendar`(단일 날짜) +
 * `TimeFields`(시각)** 를 함께 띄운다. 새 메커니즘이 아니라 기존 primitive **조립** — 날짜는 RFC-date-picker
 * `Calendar`+`useDateSelection`, 시각은 RFC-time-input에서 추출한 `TimeFields`를 재사용한다. 값은
 * `{ date, time }`(부분 선택 허용). 날짜→시각 순서라 Cancel/OK 확정이 기본이고, OK에서 시각을
 * step/min·max로 정규화(스냅+안내문 재확인)한다. DatePicker/TimeInput과 트리거/토글 인프라 공유.
 */
export const DateTimePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  isDateDisabled,
  minTime,
  maxTime,
  minDateTime,
  maxDateTime,
  minuteStep = 1,
  locale,
  weekStartsOn,
  disabled = false,
  label,
  size = "md",
  placement = "bottom-start",
  id,
  className,
  placeholder = "Select date & time",
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  showNow = true,
  nowLabel = "Now",
  adjustNotice,
  renderTrigger,
}: DateTimePickerProps) => {
  const popup = useAnchoredPopupAnchor();
  const trigger = useTriggerToggle(popup, disabled);
  const resolvedWeekStart = weekStartsOn ?? getLocaleWeekStart(locale);

  // 편집 중 draft(스테이징). 팝업 열릴 때 value로 시드, OK에서 커밋.
  const [draftDate, setDraftDate] = useState<Date | null>(value.date);
  const [draftTime, setDraftTime] = useState<TimeValue | null>(value.time);
  const [notice, setNotice] = useState<ReactNode>(null);
  const [now, setNow] = useState<TimeValue>(() => nowTime());
  const [viewDate, setViewDate] = useState<Date>(() => startOfDay(value.date ?? new Date()));

  // 팝업 열릴 때 draft/안내문/now/표시 달을 현재 value로 리셋.
  useEffect(() => {
    if (popup.open) {
      setDraftDate(value.date);
      setDraftTime(value.time);
      setNotice(null);
      setNow(nowTime());
      setViewDate(startOfDay(value.date ?? new Date()));
    }
    // 열림 전이에서만 seeding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup.open]);

  const selection = useDateSelection({ value: draftDate, onChange: setDraftDate });

  // 시간 하/상한 = 매일 적용되는 min/maxTime + (경계일에서만) 인스턴트 경계.
  const effMinTime = laterTime(minTime ?? null, boundaryTime(minDateTime, draftDate));
  const effMaxTime = earlierTime(maxTime ?? null, boundaryTime(maxDateTime, draftDate));

  const canCommit = draftDate != null && draftTime != null;

  const commit = () => {
    if (!draftDate || !draftTime) return;
    // 24:00("오늘의 끝")은 스냅 없이 실제 다음날 00:00으로 커밋(canonical [start,end) 경계).
    if (isEndOfDay(draftTime)) {
      onChange(toActual({ date: draftDate, time: draftTime }));
      popup.close();
      return;
    }
    const normalized = normalizeTime(draftTime, { min: effMinTime, max: effMaxTime, minuteStep });
    const adjusted =
      normalized.hours !== draftTime.hours || normalized.minutes !== draftTime.minutes;
    if (adjusted) {
      // 시각을 가장 가까운 유효 값으로 스냅 + 안내문. 팝업 유지(재확인 후 다시 OK).
      setDraftTime(normalized);
      setNotice(
        adjustNotice
          ? adjustNotice(normalized)
          : `Adjusted to nearest valid time (${formatTime(normalized)})`,
      );
      return;
    }
    onChange({ date: draftDate, time: normalized });
    popup.close();
  };

  const triggerText = useMemo(() => {
    const d = value.date ? formatDate(value.date, locale) : null;
    const t = value.time ? formatTime(value.time) : null;
    if (d && t) return `${d} ${t}`;
    return d ?? t ?? null;
  }, [value.date, value.time, locale]);

  return (
    <PickerRoot className={className}>
      {renderTrigger ? (
        renderTrigger({ ...trigger, isOpen: popup.open, value, disabled })
      ) : (
        <Field label={label} layout="stack" disabled={disabled} id={id}>
          <PickerTrigger
            disabled={disabled}
            placeholder={value.date == null && value.time == null}
            size={size}
            {...trigger.triggerProps}
          >
            <span>{triggerText ?? placeholder}</span>
            <CalendarIcon aria-hidden="true" />
          </PickerTrigger>
        </Field>
      )}

      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        placement={placement}
        role="dialog"
        aria-label={typeof label === "string" ? label : "Choose date and time"}
        // 달력 + 시각 열 좌우 배치 폭 수용 — 기본 max-width(28rem)면 시각 열이 잘린다.
        maxWidth="min(32rem, calc(100vw - 1rem))"
      >
        <AnchoredPopupBody padding="md">
          {/* 날짜(좌) / 시각(우) 좌우 배치, 세로 구분선으로 나눔. */}
          <Body>
            <Calendar
              viewDate={viewDate}
              onViewDateChange={setViewDate}
              weekStartsOn={resolvedWeekStart}
              locale={locale}
              min={minDate}
              max={maxDate}
              isDateDisabled={isDateDisabled}
              selection={selection}
              onSelectDate={selection.selectDate}
              initialFocusDate={draftDate ?? new Date()}
              aria-label="Date"
            />
            <TimeColumn>
              <TimeFields
                value={draftTime}
                onChange={setDraftTime}
                min={effMinTime}
                max={effMaxTime}
                minuteStep={minuteStep}
                placeholderTime={now}
                showNow={showNow}
                nowLabel={nowLabel}
                allow24
              />
              {notice ? <Notice role="status">{notice}</Notice> : null}
            </TimeColumn>
          </Body>
        </AnchoredPopupBody>
        <AnchoredPopupFooter align="between">
          {/* 확정 전 스테이징된 선택(날짜+시각) 미리보기. */}
          <Preview>
            {draftDate ? formatDate(draftDate, locale) : "—"}{" "}
            {draftTime ? formatTime(draftTime) : "--:--"}
          </Preview>
          <FooterActions>
            <IconButton variant="ghost" text={cancelLabel} onClick={popup.close} />
            <IconButton
              variant="solid"
              tone="primary"
              text={confirmLabel}
              disabled={!canCommit}
              onClick={commit}
            />
          </FooterActions>
        </AnchoredPopupFooter>
      </AnchoredPopup>
    </PickerRoot>
  );
};

// 날짜/시각 좌우 배치.
const Body = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

// 시각 열(우) — 달력과 세로 구분선으로 나눈다.
const TimeColumn = styled.div`
  align-self: stretch;
  min-width: 11rem;
  padding-left: 1rem;
  border-left: 1px solid ${({ theme }) => theme.color.border.subtle};
`;

// 조정 안내문 — 경고 톤 + role=status(aria-live)로 스냅 결과를 낭독.
const Notice = styled.div`
  margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.typography.size.caption};
  color: ${({ theme }) => theme.color.text.warning};
`;

// 확정 전 미리보기 — 푸터 왼쪽, 숫자 폭 고정(tabular)으로 안정적.
const Preview = styled.div`
  font-size: ${({ theme }) => theme.typography.size.body};
  color: ${({ theme }) => theme.color.text.subtle};
  font-variant-numeric: tabular-nums;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;
